
    import {Inject, Options, Ref, Vue} from 'vue-property-decorator';
    import InputLocation from '@/common/components/location/InputLocation.vue';
    import {Address} from '@/common/components/location/types/Address';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {CreationFormValidation} from '@/profile-creation-wizard/components/for-creation/ProfileCreationForm';
    import {Getter} from 'vuex-class';
    import getters from '@/profile-creation-wizard/components/for-creation/store/getters';
    import type {ProfileCreationForm, WorkplacePreference} from '@/profile-creation-wizard/components/for-creation/store';
    import mutations from '@/profile-creation-wizard/components/for-creation/store/mutations';
    import {BlockedCountryRepository} from '@/common/repositories/blocked-countries/BlockedCountryRepository';
    import type {BlockedCountriesList} from '@/common/repositories/blocked-countries/BlockedCountriesList';
    import {TipsPart} from '@/profile-creation-wizard/components/for-creation/TipsPart';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import BottomSheetDisplayer from '@/common/ui/BottomSheetDisplayer';

    @Options({
        components: {
            InputLocation,
        },
    })
    export default class LocationAndWorkplace extends Vue {
        @Inject({from: DI.blockedCountriesRepository})
        blockedCountryRepository!: BlockedCountryRepository;
        @Inject({from: DI.creationFormValidation})
        creationFormValidation!: CreationFormValidation;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        @Ref('inputLocation')
        inputLocation!: InputLocation;
        address?: Address;
        workPlacePreference: WorkplacePreference | null = null;

        blockedCountriesList: BlockedCountriesList = [];
        blockedCountry = false;

        bottomSheetSelector = '#details-location-bottom-sheet';
        dataHeapTooltipID = 'profile-creation-tooltip-location';

        async mounted(): Promise<void> {
            const blockedCountries = await this.blockedCountryRepository.getAll();
            this.blockedCountriesList = blockedCountries || [];

            this.bottomSheetDisplayer = new BottomSheetDisplayer(
                this.bottomSheetSelector,
                this.dataHeapTooltipID,
                this.$el,
                'div.profile-creation-wizard',
            );
            this.bottomSheetDisplayer.mounted();
            this.workPlacePreference = this.creationForm.workPlacePreference;
        }

        openBottomSheet(): void {
            this.bottomSheetDisplayer.openBottomSheet();
        }

        updateAddress(newAddress: Address): void {
            this.address = newAddress;
            this.$store.commit(mutations.selectAddress, this.address);

            this.blockedCountry = this.inputLocation.isBlockedCountry;
            this.$emit('change');
        }

        updateWorkplace(newWorkplaceCustomEvent: CustomEvent): void {
            this.emitFocus();
            this.workPlacePreference = newWorkplaceCustomEvent.detail.value;
            this.$store.commit(mutations.selectWorkplacePreference, this.workPlacePreference);
        }

        validateAddress(): void {
            // AS-931: Validate address while updating input value
            // Prevent from being able to validate form without a valid address
            this.inputLocation.validate();
            if (!this.inputLocation.inputLocation.value) {
                this.address = undefined;
                this.$store.commit(mutations.selectAddress, undefined);
            }
        }

        isWorkplaceSelected(workplaceSelected: string) {
            return this.workPlacePreference === workplaceSelected;
        }

        emitFocus(): void {
            eventBusProfileCreationWizard.emit('creation:focus-changed', TipsPart.locationAndWorkplace);
        }

        get isBlockedCountry(): boolean {
            return this.blockedCountry;
        }

        get defaultLocation() {
            return this.creationForm.address;
        }

        get workplaces(): {[keys: string]: {[keys: string]: string}} {
            return {
                // PARTIALLY_ONSITE -> not available
                ONSITE: {
                    label: this.$msg('profile.creation.completion.wizard.details.location.step2.workplace.hybrid'),
                    value: 'ONSITE',
                },
                REMOTE: {
                    label: this.$msg('profile.creation.completion.wizard.details.location.step2.workplace.remote'),
                    value: 'REMOTE',
                },
            };
        }
    }
